import React, { useState, useEffect, useCallback } from 'react';
import {
  message,
  Card,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Row,
  Col,
  Avatar,
  Typography,
  Table,
  Tag,
  Modal,
  Popconfirm,
  Switch,
  Space,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CalendarOutlined,
  SearchOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../server/server';
import { getActiveCountries } from '../../server/configuration/FiltersServer';

const { Option } = Select;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const DAYS_OF_WEEK_FULL = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const EventMiniManager = ({ profiles, profilesLoading, musicGenres, searchProfiles }) => {
  // List + filter state
  const [eventMinis, setEventMinis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [search, setSearch] = useState('');
  const [recurringOnly, setRecurringOnly] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [musicTypeFilter, setMusicTypeFilter] = useState(null);
  const [creatorFilter, setCreatorFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  // Profile search state for creator filter + edit modal
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Countries / cities for edit modal
  const [countries, setCountries] = useState([]);
  const [editCities, setEditCities] = useState([]);

  // Edit modal state
  const [editForm] = Form.useForm();
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editIsRecurring, setEditIsRecurring] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const fetchEventMinis = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        sort: 'newest',
      };
      if (search?.trim()) params.search = search.trim();
      if (musicTypeFilter) params.musicType = musicTypeFilter;
      if (creatorFilter) params.creater = creatorFilter;
      if (!includeInactive) params.isActive = true;
      if (recurringOnly) params.isRecurring = true;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].startOf('day').toISOString();
        params.endDate = dateRange[1].endOf('day').toISOString();
      }

      const response = await apiClient.get('/discovery/event-minis', { params });
      const payload = response.data?.data ?? response.data ?? [];
      const list = Array.isArray(payload) ? payload : payload.data || [];
      const totalCount =
        response.data?.total ??
        response.data?.pagination?.total ??
        response.data?.meta?.total ??
        list.length;

      setEventMinis(list);
      setTotal(totalCount);
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to load EventMinis';
      message.error(msg);
      setEventMinis([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, musicTypeFilter, creatorFilter, includeInactive, recurringOnly, dateRange]);

  useEffect(() => {
    fetchEventMinis();
  }, [fetchEventMinis]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getActiveCountries();
        if (response?.data) setCountries(response.data);
      } catch (e) {
        // non-fatal
      }
    })();
  }, []);

  useEffect(() => {
    if (profiles && profiles.length > 0 && searchResults.length === 0) {
      setSearchResults(profiles);
    }
  }, [profiles]);

  useEffect(() => () => searchTimeout && clearTimeout(searchTimeout), [searchTimeout]);

  const debouncedSearch = useCallback(
    async (term) => {
      if (!searchProfiles) return;
      setSearchLoading(true);
      try {
        const results = await searchProfiles(term);
        setSearchResults(results);
      } catch (e) {
        message.error('Failed to search profiles');
      } finally {
        setSearchLoading(false);
      }
    },
    [searchProfiles]
  );

  const handleProfileSearch = (value) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    if (!value || !value.trim()) {
      setSearchResults(profiles);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const t = setTimeout(() => debouncedSearch(value.trim()), 300);
    setSearchTimeout(t);
  };

  const visibleEventMinis = eventMinis;

  const openEdit = (record) => {
    setEditing(record);
    setEditIsRecurring(!!record.isRecurring);

    const countryId =
      typeof record.city?.country === 'object'
        ? record.city.country?._id
        : record.city?.country || null;

    const country = countries.find((c) => c._id === countryId);
    setEditCities(country?.cities || []);

    editForm.setFieldsValue({
      title: record.title,
      creater: record.creater?._id || record.creater,
      musicType: record.musicType?._id || record.musicType || undefined,
      startTime: record.startTime ? dayjs(record.startTime) : null,
      endTime: record.endTime ? dayjs(record.endTime) : null,
      country: countryId || undefined,
      city: record.city?._id || record.city || undefined,
      flyer: record.flyer || undefined,
      isRecurring: !!record.isRecurring,
      daysOfWeek: record.daysOfWeek || [],
      affiliatedProfiles: (record.affiliatedProfiles || []).map((p) => p?._id || p),
      isActive: record.isActive !== false,
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);
    setEditIsRecurring(false);
    editForm.resetFields();
  };

  const handleEditCountryChange = (countryId) => {
    const country = countries.find((c) => c._id === countryId);
    setEditCities(country?.cities || []);
    editForm.setFieldsValue({ city: null });
  };

  const handleSaveEdit = async (values) => {
    if (!editing?._id) return;
    setEditSaving(true);
    try {
      const payload = {
        title: values.title,
        startTime: values.startTime?.toISOString(),
        endTime: values.endTime?.toISOString(),
        musicType: values.musicType || null,
        city: values.city || null,
        flyer: values.flyer || null,
        affiliatedProfiles: values.affiliatedProfiles || [],
        isRecurring: !!values.isRecurring,
        daysOfWeek: values.isRecurring ? values.daysOfWeek || [] : [],
        isActive: values.isActive !== false,
      };
      await apiClient.put(`/discovery/event-minis/${editing._id}`, payload);
      message.success('EventMini updated');
      closeEdit();
      fetchEventMinis();
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to update EventMini';
      message.error(msg);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await apiClient.delete(`/discovery/event-minis/${record._id}`);
      message.success('EventMini deleted');
      fetchEventMinis();
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to delete EventMini';
      message.error(msg);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await apiClient.put(`/discovery/event-minis/${record._id}`, {
        isActive: !record.isActive,
      });
      message.success(record.isActive ? 'EventMini deactivated' : 'EventMini activated');
      fetchEventMinis();
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to toggle status';
      message.error(msg);
    }
  };

  const columns = [
    {
      title: 'Event',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            src={record.creater?.picture}
            size={40}
            style={{ flexShrink: 0 }}
          >
            {record.creater?.username?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{title}</span>
              {record.isRecurring && (
                <Tag color="blue" icon={<CalendarOutlined />} style={{ marginRight: 0 }}>
                  Recurring
                </Tag>
              )}
              {record.isActive === false && <Tag color="red">Inactive</Tag>}
            </div>
            <div style={{ fontSize: 12, color: '#888' }}>
              by {record.creater?.username || 'Unknown'}
              {record.creater?.isVerified && (
                <Tag color="gold" style={{ marginLeft: 6 }}>
                  Verified
                </Tag>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'When',
      key: 'when',
      width: 230,
      render: (_, record) => (
        <div>
          <div>
            {dayjs(record.startTime).format('MMM D, YYYY')} ·{' '}
            {dayjs(record.startTime).format('h:mm A')} – {dayjs(record.endTime).format('h:mm A')}
          </div>
          {record.isRecurring && record.daysOfWeek?.length > 0 && (
            <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {record.daysOfWeek
                .slice()
                .sort((a, b) => a - b)
                .map((d) => (
                  <Tag key={d} color="geekblue" style={{ marginRight: 0 }}>
                    {DAYS_OF_WEEK.find((x) => x.value === d)?.label}
                  </Tag>
                ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Music',
      key: 'music',
      width: 130,
      render: (_, record) =>
        record.musicType ? (
          <Tag color="purple">{record.musicType.value || record.musicType}</Tag>
        ) : (
          <span style={{ color: '#bbb' }}>—</span>
        ),
    },
    {
      title: 'City',
      key: 'city',
      width: 140,
      render: (_, record) => record.city?.name || <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Affiliated',
      key: 'affiliated',
      width: 160,
      render: (_, record) => {
        const list = record.affiliatedProfiles || [];
        if (!list.length) return <span style={{ color: '#bbb' }}>—</span>;
        const shown = list.slice(0, 2);
        const more = list.length - shown.length;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {shown.map((p, i) => (
              <Tag key={p?._id || i} style={{ marginRight: 0 }}>
                {p?.username || p}
              </Tag>
            ))}
            {more > 0 && <Tag style={{ marginRight: 0 }}>+{more}</Tag>}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.isActive === false ? 'Activate' : 'Deactivate'}>
            <Button
              size="small"
              icon={record.isActive === false ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this EventMini?"
            description={
              record.isRecurring
                ? 'This deletes the entire recurring series.'
                : 'This action cannot be undone.'
            }
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>
        <CalendarOutlined /> EventMini Manager
      </Title>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search by title"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filter by creator"
              showSearch
              allowClear
              loading={profilesLoading || searchLoading}
              filterOption={false}
              onSearch={handleProfileSearch}
              onChange={(value) => {
                setCreatorFilter(value || null);
                setPage(1);
              }}
              style={{ width: '100%' }}
              options={searchResults.map((p) => ({
                value: p._id,
                label: (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar src={p.picture} size={20} style={{ marginRight: 8 }} />
                    <span>{p.username}</span>
                  </div>
                ),
              }))}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Music type"
              allowClear
              showSearch
              optionFilterProp="label"
              value={musicTypeFilter}
              onChange={(value) => {
                setMusicTypeFilter(value || null);
                setPage(1);
              }}
              style={{ width: '100%' }}
              options={(musicGenres || [])
                .slice()
                .sort((a, b) => a.value.localeCompare(b.value))
                .map((g) => ({ value: g._id, label: g.value }))}
            />
          </Col>
          <Col xs={24} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                setPage(1);
              }}
            />
          </Col>
        </Row>
        <Row gutter={[12, 12]} style={{ marginTop: 12 }} align="middle">
          <Col>
            <Space>
              <Switch
                checked={recurringOnly}
                onChange={(v) => {
                  setRecurringOnly(v);
                  setPage(1);
                }}
              />
              <span>Recurring only</span>
            </Space>
          </Col>
          <Col>
            <Space>
              <Switch
                checked={includeInactive}
                onChange={(v) => {
                  setIncludeInactive(v);
                  setPage(1);
                }}
              />
              <span>Include inactive</span>
            </Space>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Button icon={<ReloadOutlined />} onClick={fetchEventMinis} loading={loading}>
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      <Card
        title={
          <span>
            EventMinis
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {visibleEventMinis.length} shown · {total} total
            </Tag>
          </span>
        }
      >
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={visibleEventMinis}
          columns={columns}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showTotal: (t) => `Total ${t} EventMinis`,
          }}
        />
      </Card>

      <Modal
        title={editing ? `Edit: ${editing.title}` : 'Edit EventMini'}
        open={editOpen}
        onCancel={closeEdit}
        onOk={() => editForm.submit()}
        okText="Save"
        confirmLoading={editSaving}
        width={780}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleSaveEdit}
          initialValues={{ isActive: true }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Event Title"
                rules={[{ required: true, message: 'Please enter event title' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="creater" label="Creator Profile">
                <Select
                  showSearch
                  allowClear
                  loading={profilesLoading || searchLoading}
                  filterOption={false}
                  onSearch={handleProfileSearch}
                  disabled
                  options={searchResults.map((p) => ({
                    value: p._id,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar src={p.picture} size={20} style={{ marginRight: 8 }} />
                        <span>{p.username}</span>
                      </div>
                    ),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="musicType" label="Music Type">
                <Select placeholder="Select music type" allowClear>
                  {(musicGenres || [])
                    .slice()
                    .sort((a, b) => a.value.localeCompare(b.value))
                    .map((type) => (
                      <Option key={type._id} value={type._id}>
                        {type.value}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label="Start Time"
                rules={[{ required: true, message: 'Please select start time' }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  className="w-full"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label="End Time"
                rules={[{ required: true, message: 'Please select end time' }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  className="w-full"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="country" label="Country">
                <Select
                  placeholder="Select country"
                  showSearch
                  allowClear
                  onChange={handleEditCountryChange}
                  filterOption={(input, option) => {
                    const country = countries.find((c) => c._id === option.value);
                    return country?.name?.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {countries.map((country) => (
                    <Option key={country._id} value={country._id}>
                      {country.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="city" label="City">
                <Select placeholder="Select city" showSearch allowClear>
                  {editCities.map((city) => (
                    <Option key={city._id} value={city._id}>
                      {city.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="flyer"
                label="Flyer URL"
                rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
              >
                <Input placeholder="https://example.com/flyer.jpg" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isRecurring" label="Recurring" valuePropName="checked">
                <Switch
                  onChange={(checked) => {
                    setEditIsRecurring(checked);
                    if (!checked) editForm.setFieldsValue({ daysOfWeek: [] });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {editIsRecurring && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="daysOfWeek"
                  label="Days of Week"
                  rules={[{ required: true, message: 'Please select at least one day' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select days this event repeats"
                    options={DAYS_OF_WEEK_FULL}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="affiliatedProfiles" label="Affiliated Profiles">
                <Select
                  mode="multiple"
                  placeholder="Search and select affiliated profiles"
                  loading={profilesLoading || searchLoading}
                  showSearch
                  onSearch={handleProfileSearch}
                  filterOption={false}
                  options={searchResults.map((profile) => ({
                    value: profile._id,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar src={profile.picture} size={20} style={{ marginRight: 8 }} />
                        <span>{profile.username}</span>
                      </div>
                    ),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default EventMiniManager;
